import {
    ALPHA_2_ISO_3166,
    ALPHA_2_ISO_3166_EU,
    LIMITED_RESIDENCE_PERMIT_TYPES,
    RESIDENCE_PERMIT_TYPES,
    SALUTATIONS,
} from 'cpool-constants';
import { Personendaten, schemaApplicantCreation } from 'cpool-schemas';
import { calculateAge } from 'cpool-utils';
import { z } from 'zod';

import { normalizeTextInput } from '~/utils/normalizeTextInput';

const MIN_APPLICANT_AGE = 18;

const optionalFormString = () =>
    z.preprocess((value) => (value === '' ? null : value), z.union([z.null(), z.string().min(1)]).default(null));

const limitedResidencePermitTypes = [
    ...LIMITED_RESIDENCE_PERMIT_TYPES,
    RESIDENCE_PERMIT_TYPES.RESIDENCE_PERMIT_SWITZERLAND,
] as unknown as string[];

const createApplicantPersonendaten = Personendaten.extend({
    anrede: z.nativeEnum(SALUTATIONS),
    geburtsdatum: z.union([z.null(), z.coerce.date()]).superRefine((date, ctx) => {
        if (date === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                params: { i18n: 'errors.invalid_type_received_null' },
            });
            return;
        }

        if (calculateAge({ birthdate: date }) < MIN_APPLICANT_AGE) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                params: { i18n: 'errors.minimumAge18' },
            });
        }
    }),
    geburtsname: optionalFormString(),
    geburtsort: optionalFormString(),
})
    .refine(
        (personendaten) => {
            if (
                !(Object.values(ALPHA_2_ISO_3166_EU) as string[]).includes(personendaten.staatsangehoerigkeit) &&
                !personendaten.aufenthaltstitel
            ) {
                return false;
            }
            return true;
        },
        {
            params: { i18n: 'errors.aufenthaltstitelRequired' },
            path: ['aufenthaltstitel'],
        },
    )
    .refine(
        (personendaten) => {
            if (
                personendaten.aufenthaltstitel &&
                limitedResidencePermitTypes.includes(personendaten.aufenthaltstitel) &&
                !personendaten.aufenthaltBefristetBis
            ) {
                return false;
            }
            return true;
        },
        {
            params: { i18n: 'errors.residencePermitUntilRequired' },
            path: ['aufenthaltBefristetBis'],
        },
    )
    .refine(
        (personendaten) => {
            if (personendaten.staatsangehoerigkeit !== ALPHA_2_ISO_3166.DE && !personendaten.inDeutschlandSeit) {
                return false;
            }
            return true;
        },
        {
            params: { i18n: 'errors.inGermanySinceRequired' },
            path: ['inDeutschlandSeit'],
        },
    );

export const schemaCreateApplicantForm = z.preprocess(
    normalizeTextInput,
    schemaApplicantCreation.extend({
        personendaten: createApplicantPersonendaten,
    }),
);

export type CreateApplicantFormValues = z.infer<typeof schemaCreateApplicantForm>;
